import { useQuery } from '@tanstack/react-query';
import { BookMarkedIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import { brainsEndpoints, collectionsEndpoints } from '@/api/endpoints';
import { CommandGroup } from '@/components/ui/command';
import { API as BrainsAPI } from '@/hooks/useBrains';
import { API as CollectionsAPI } from '@/hooks/useCollections';
import { selectAccountSlug } from '@/redux/session/selectors';
import { type Brains } from '@/shared/types/brain';
import { type Collections } from '@/shared/types/collections';

import { CommandLinkItem } from '../CommandLinkItem';
import { getBrainIcon } from '../icons';
import { type PageDrillDown } from '../types';
import { buildEntityValue, ENTITY_TYPE_LABELS } from '../utils';
import { TabHint } from './TabHint';

type EntityListGroupProps = {
  type: 'ai_agent' | 'knowledge_base';
  onSelect: (path: string, label: string) => void;
  highlightedValue?: string;
  pageDrillDown: PageDrillDown | null;
};

export const EntityListGroup = ({
  type,
  onSelect,
  highlightedValue,
  pageDrillDown,
}: EntityListGroupProps) => {
  const { t } = useTranslation();
  const slug = useSelector(selectAccountSlug);

  const { data: brainsData } = useQuery<Brains>({
    queryKey: [brainsEndpoints.brains],
    queryFn: () => BrainsAPI.listBrains(),
    enabled: type === 'ai_agent',
  });

  const { data: collectionsData } = useQuery<Collections>({
    queryKey: [collectionsEndpoints.collections],
    queryFn: () => CollectionsAPI.listAllCollections(),
    enabled: type === 'knowledge_base',
  });

  const items =
    type === 'ai_agent'
      ? (brainsData?.brains ?? []).map((b) => ({
          id: b.brain_id,
          name: b.name,
          path: `/ai-agents/${b.brain_id}/overview`,
          icon: getBrainIcon(b.brain_type),
        }))
      : (collectionsData?.collections ?? []).map((c) => ({
          id: c.collection_id,
          name: c.name,
          path: `/knowledge-bases/${c.collection_id}/category/all`,
          icon: BookMarkedIcon,
        }));

  return (
    <CommandGroup heading={t(ENTITY_TYPE_LABELS[type])}>
      {items.map((item) => {
        const Icon = item.icon;
        const value = buildEntityValue(type, item.id);
        return (
          <CommandLinkItem
            key={item.id}
            value={value}
            href={slug ? `/${slug}${item.path}` : '#'}
            onSelect={() => onSelect(item.path, item.name)}
            keywords={[item.name]}
          >
            <Icon />
            <span className="flex-1 truncate">{item.name}</span>
            <TabHint
              value={value}
              highlightedValue={highlightedValue}
              pageDrillDown={pageDrillDown}
            />
          </CommandLinkItem>
        );
      })}
    </CommandGroup>
  );
};
